import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Fields from '../../../services/fields';
import Section from '../Section/Section';
import FilterAutoComplete from '../FilterAutoComplete/FilterAutoComplete';
import { FILTERS_OPTIONS, getAvailableOptions } from '../../consts';
import css from './FiltersSection.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.FILTERS';

const fieldsService = new Fields();

const FiltersSection = ({ filtersRef, onChange }) => {
    const [filters, setFilters] = useState(() => filtersRef.current.filters);
    const [filterFieldOptions, setFilterFieldOptions] = useState({});
    const [filterFieldsLoading, setFilterFieldsLoading] = useState(false);
    const [filterFieldsError, setFilterFieldsError] = useState(false);

    useEffect(() => {
        filtersRef.current.filters = filters;
    }, [filters, filtersRef]);

    useEffect(() => {
        setFilterFieldsLoading(true);
        fieldsService
            .updateFilterFields()
            .then(response => {
                const fields = response.value || [];
                const optionsMap = FILTERS_OPTIONS.reduce((acc, { value, apiName }) => {
                    const field = fields.find(f => f.name === apiName);
                    acc[value] = (field?.values || []).map(fieldValue => ({
                        name: fieldValue.name,
                        display_name: fieldValue.display_name || fieldValue.name,
                    }));
                    return acc;
                }, {});
                setFilterFieldOptions(optionsMap);
            })
            .catch(() => {
                setFilterFieldsError(true);
            })
            .finally(() => setFilterFieldsLoading(false));
    }, []);

    const getFilterAutoCompleteOptions = useCallback(
        filterType => {
            const fields = filterFieldOptions[filterType] || [];
            return fields.map(option => ({ label: option.display_name, value: option.name }));
        },
        [filterFieldOptions]
    );

    const handleAddFilter = useCallback(() => {
        setFilters(prev => {
            const option = getAvailableOptions(
                FILTERS_OPTIONS,
                prev.map(f => f.type),
                null
            )[0];
            return [...prev, { type: option.value, values: [] }];
        });
    }, []);

    const handleFilterChange = useCallback((value, currentType) => {
        if (!value) return;
        setFilters(prev => prev.map(f => (f.type === currentType ? { type: value.value, values: [] } : f)));
    }, []);

    const handleRemoveFilter = useCallback(
        type => {
            setFilters(prev => {
                const next = prev.filter(f => f.type !== type);
                filtersRef.current.filters = next;
                return next;
            });
            onChange();
        },
        [filtersRef, onChange]
    );

    const handleFilterValueChange = useCallback(
        (type, values) => {
            setFilters(prev => {
                const next = prev.map(f => (f.type === type ? { ...f, values: values || [] } : f));
                filtersRef.current.filters = next;
                return next;
            });
            onChange();
        },
        [filtersRef, onChange]
    );

    const addFilterButton = useMemo(
        () => ({
            text: 'STATIC.FILTERS_SECTION.ADD_FILTER',
            onClick: handleAddFilter,
            disabled: filters.length >= FILTERS_OPTIONS.length,
        }),
        [handleAddFilter, filters.length]
    );

    return (
        <Section
            icon="filter"
            labelKey="STATIC.FILTERS"
            errorKey={filterFieldsError ? `${TRANSLATION_PREFIX}.LOAD_ERROR` : undefined}
            addButton={addFilterButton}
        >
            {filters.map(filter => (
                <div key={filter.type} className={css.filterBlock}>
                    <FilterAutoComplete
                        options={getAvailableOptions(
                            FILTERS_OPTIONS,
                            filters.map(f => f.type),
                            filter.type
                        )}
                        value={FILTERS_OPTIONS.find(o => o.value === filter.type)}
                        onChange={value => handleFilterChange(value, filter.type)}
                        onRemove={() => handleRemoveFilter(filter.type)}
                        containerClass={css.filterAutocomplete}
                    />
                    <FilterAutoComplete
                        options={getFilterAutoCompleteOptions(filter.type)}
                        value={filter.values}
                        onChange={values => handleFilterValueChange(filter.type, values)}
                        label={`${TRANSLATION_PREFIX}.VALUES_PLACEHOLDER`}
                        isMulti
                        loading={filterFieldsLoading}
                        containerClass={css.filterAutocomplete}
                    />
                </div>
            ))}
        </Section>
    );
};

FiltersSection.propTypes = {
    filtersRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    onChange: PropTypes.func,
};

export default FiltersSection;
